/**
 * Emoji-Reaktionen der Entwickler an Martins Blasen. (18.10.2026, shared/reaktionen.ts, server/reaktionen.ts)
 *
 * Der Server schickt den ganzen Stand (Ereignis `reaktionen`, auch nach jedem Neuverbinden). Dieses Modul hängt je Reaktion einen
 * kleinen Chip unten rechts an die Blase von Martin, die dem Zeitpunkt der Reaktion am nächsten liegt (`.bubble.user[data-ts]`,
 * höchstens ZIEL_TOLERANZ_MS entfernt — sonst bleibt die Reaktion unsichtbar statt an der falschen Blase zu stehen).
 * Je Entwickler ein Chip; ein neues Emoji ersetzt das alte, weil der Stand es so liefert. Der Chip zeigt das Emoji und das Kürzel
 * des Entwicklers, in seiner Farbe.
 *
 * ⚠️ Kein Chat-Beitrag: nichts im Verlauf, nie vorgelesen, kein Mikro, keine Zählung. Ein Emoji ist nie Martins Ja.
 * ⚠️ Barrierefreiheit: jeder Chip hat `role="img"` und ein `aria-label` („Schmied reagiert mit Daumen hoch“); die Zeichen darin
 *    sind für Bildschirmleser versteckt, damit nichts doppelt gelesen wird. Texte nur über textContent.
 */
import { entwickler, type EntwicklerId } from "../../shared/entwickler.js";
import { reaktionBeschriftung, type Reaktion } from "../../shared/reaktionen.js";

export const REAKTIONEN_KLASSE = "bubble-reaktionen";
/** So weit darf die Blase vom Zeitpunkt der Nachricht abweichen (Uhren Gerät/Server, Sprach-Erkennung). */
export const ZIEL_TOLERANZ_MS = 120_000;

/** Das Kürzel im Chip: erster Buchstabe des Namens. */
function kuerzel(id: EntwicklerId): string {
  return entwickler(id).name.slice(0, 1).toUpperCase();
}

/** Die Blase von Martin, die der Zeit am nächsten liegt (oder null). Rein bis auf das Lesen des DOM. */
export function findeZielBlase(wurzel: ParentNode, zeit: string): HTMLElement | null {
  const ziel = Date.parse(zeit);
  if (!Number.isFinite(ziel)) return null;
  let beste: HTMLElement | null = null;
  let abstand = Number.POSITIVE_INFINITY;
  for (const b of wurzel.querySelectorAll<HTMLElement>(".bubble.user[data-ts]")) {
    const d = Math.abs(Number(b.dataset.ts) - ziel);
    if (Number.isFinite(d) && d < abstand) {
      abstand = d;
      beste = b;
    }
  }
  return beste && abstand <= ZIEL_TOLERANZ_MS ? beste : null;
}

function baueChip(doc: Document, r: Reaktion): HTMLElement {
  const chip = doc.createElement("span");
  chip.className = "reaktion-chip";
  chip.dataset.von = r.von;
  chip.setAttribute("role", "img");
  chip.setAttribute("aria-label", reaktionBeschriftung(entwickler(r.von).name, r.emoji));
  const emoji = doc.createElement("span");
  emoji.className = "reaktion-emoji";
  emoji.setAttribute("aria-hidden", "true");
  emoji.textContent = r.emoji;
  const wer = doc.createElement("span");
  wer.className = "reaktion-kuerzel";
  wer.setAttribute("aria-hidden", "true");
  wer.textContent = kuerzel(r.von);
  chip.append(emoji, wer);
  return chip;
}

/**
 * Zeichnet den Stand neu: erst alle alten Chips weg, dann je Reaktion ihr Chip an die Zielblase. Idempotent — beliebig oft
 * aufrufbar (nach dem Stand vom Strom und nach dem Nachladen des Verlaufs).
 */
export function zeichneReaktionen(wurzel: ParentNode, reaktionen: readonly Reaktion[]): void {
  for (const alt of wurzel.querySelectorAll(`.${REAKTIONEN_KLASSE}`)) alt.remove();
  const doc = (wurzel as Node).ownerDocument ?? (wurzel as Document);
  const jeBlase = new Map<HTMLElement, Reaktion[]>();
  for (const r of [...reaktionen].sort((a, b) => a.um - b.um)) {
    const blase = findeZielBlase(wurzel, r.zeit);
    if (!blase) continue;
    jeBlase.set(blase, [...(jeBlase.get(blase) ?? []), r]);
  }
  for (const [blase, liste] of jeBlase) {
    const zeile = doc.createElement("div");
    zeile.className = REAKTIONEN_KLASSE;
    zeile.setAttribute("role", "group");
    zeile.setAttribute("aria-label", "Reaktionen der Entwickler");
    for (const r of liste) zeile.appendChild(baueChip(doc, r));
    blase.appendChild(zeile);
  }
}

/**
 * Hält den letzten Stand und zeichnet ihn neu, wenn im Verlauf Blasen dazukommen (Nachladen, Neuladen) — die Chips stehen so auch
 * dann, wenn der Stand vor den Blasen ankam. Gibt zum Beenden eine Abmeldung zurück.
 */
export function baueReaktionenAnzeige(wurzel: HTMLElement): { setze(reaktionen: readonly Reaktion[]): void; ende(): void } {
  let stand: readonly Reaktion[] = [];
  let zeichnet = false;
  const neu = (): void => {
    zeichnet = true;
    try {
      zeichneReaktionen(wurzel, stand);
    } finally {
      zeichnet = false;
    }
  };
  let wartet = false;
  const beobachter =
    typeof MutationObserver === "function"
      ? new MutationObserver(() => {
          // Die eigenen Änderungen lösen keine neue Runde aus; sonst höchstens einmal je Tick.
          if (zeichnet || wartet || stand.length === 0) return;
          wartet = true;
          queueMicrotask(() => {
            wartet = false;
            neu();
          });
        })
      : null;
  beobachter?.observe(wurzel, { childList: true });
  return {
    setze(reaktionen) {
      stand = reaktionen;
      neu();
    },
    ende() {
      beobachter?.disconnect();
    },
  };
}
